import { css } from '@emotion/core';
import { CircularProgress, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@material-ui/core';
import { ArrowDropDown, RateReview, Send } from '@material-ui/icons';
import * as React from 'react';

import { canUpdateSearch } from 'shared/models/search';

import { useReduxState } from '../hooks/use-redux';
import { useSession } from '../hooks/use-session';
import { Job, Search, State } from '../state';

const autoSendOnColor = '#43a047';
const reviewRequiredColor = '#f57c00';
const spinnerSize = 18;

const toggleStyles = css`
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    border-radius: 4px;
    padding: 2px;

    &:hover {
        background: rgba(0, 0, 0, 0.06);
    }

    .auto-send-state-icon {
        font-size: 18px;
    }

    .auto-send-dropdown-arrow {
        color: rgba(0, 0, 0, 0.4);
    }
`;

const menuItemIconStyles = css`
    min-width: 36px;
`;

interface SearchAutoSendToggleProps {
    search: Search;
    job: Job;
    tooltip: NonNullable<React.ReactNode>;
    onUpdateSearch: (id: string, updates: Partial<Search>, refetchSearches: boolean) => void;
}

export const SearchAutoSendToggle: React.FC<SearchAutoSendToggleProps> = ({ search, job, tooltip, onUpdateSearch }) => {
    const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
    const { user, userPermissions } = useSession();
    const pendingRequests = useReduxState((state: State) => state.pendingRequests);
    const updating = pendingRequests.has(`search-updates-${search.id}`);
    const canEdit = canUpdateSearch(search, user.id, userPermissions, job?.accountManagerId);

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
        if (canEdit && !updating) {
            setMenuAnchor(event.currentTarget);
        }
    };

    const handleCloseMenu = () => {
        setMenuAnchor(null);
    };

    const handleSelectAutoSend = () => {
        if (search.reviewRequiredForOutreach) {
            onUpdateSearch(search.id, { reviewRequiredForOutreach: false }, false);
        }
        setMenuAnchor(null);
    };

    const handleSelectReview = () => {
        if (!search.reviewRequiredForOutreach) {
            onUpdateSearch(search.id, { reviewRequiredForOutreach: true }, false);
        }
        setMenuAnchor(null);
    };

    const alwaysVisible = search.reviewRequiredForOutreach || updating;
    const containerClassName = alwaysVisible ? 'search-auto-send-toggle always-visible' : 'search-auto-send-toggle';
    const stateIcon = search.reviewRequiredForOutreach ? (
        <RateReview className="auto-send-state-icon" htmlColor={reviewRequiredColor} />
    ) : (
        <Send className="auto-send-state-icon" htmlColor={autoSendOnColor} />
    );
    const stateIndicator = updating ? <CircularProgress size={spinnerSize} /> : stateIcon;
    const dropdownArrow = canEdit && !updating ? <ArrowDropDown className="auto-send-dropdown-arrow" /> : null;

    const autoSendItem = (
        <MenuItem dense={true} selected={!search.reviewRequiredForOutreach} onClick={handleSelectAutoSend}>
            <ListItemIcon css={menuItemIconStyles}>
                <Send fontSize="small" htmlColor={autoSendOnColor} />
            </ListItemIcon>
            <ListItemText primary="Auto-send outreach" secondary="New results are sent outreach automatically" />
        </MenuItem>
    );
    const reviewItem = (
        <MenuItem dense={true} selected={search.reviewRequiredForOutreach} onClick={handleSelectReview}>
            <ListItemIcon css={menuItemIconStyles}>
                <RateReview fontSize="small" htmlColor={reviewRequiredColor} />
            </ListItemIcon>
            <ListItemText primary="Review before sending" secondary="Results are held until sent manually" />
        </MenuItem>
    );
    const menu = canEdit ? (
        <Menu open={Boolean(menuAnchor)} anchorEl={menuAnchor} onClose={handleCloseMenu}>
            {autoSendItem}
            {reviewItem}
        </Menu>
    ) : null;

    return (
        <span className={containerClassName}>
            <Tooltip title={tooltip}>
                <span css={toggleStyles} onClick={handleOpenMenu}>
                    {stateIndicator}
                    {dropdownArrow}
                </span>
            </Tooltip>
            {menu}
        </span>
    );
};
